import type { ElementType, HTMLAttributes } from "react";
import SITE_COPY from "@/copy/site-copy.json";

/**
 * Marketing-site copy (the prototype's WP_COPY, docs/COPY-CONFIG.md), the sibling of the app's
 * `copy()` in src/copy/copy.tsx. Keys are `page.section.element`. String values are authored,
 * trusted HTML (`<em>`, `<span class="st-g">`, links), so <SiteCopy> renders them as HTML. Never
 * put user input into a value.
 *
 * A handful of keys hold arrays or numbers (the priorities demo rows, the action reel, the
 * employer stats); read those with `siteData()`.
 *
 * Keys listed in the prototype's config/wp-copy-deprecated.txt are left out of site-copy.json,
 * except keys the site reads as data through `siteData()` (the reel, the priorities demo, the hero
 * cycle, the employer lists, `webinar.*`, the per-page FAQs `pagefaq.*`) and `pricingEmp.*`: the
 * list is generated from `data-c` attributes, so keys read as data appear in it by mistake. `faq.*`
 * stays while the FAQ is held.
 */
type Strings<T, P extends string = ""> = {
  [K in keyof T & string]: T[K] extends string
    ? `${P}${K}`
    : T[K] extends readonly unknown[]
      ? never
      : T[K] extends object
        ? Strings<T[K], `${P}${K}.`>
        : never;
}[keyof T & string];

export type SiteCopyKey = Strings<typeof SITE_COPY>;

function lookup(path: string): unknown {
  return path.split(".").reduce<unknown>((node, key) => {
    if (node && typeof node === "object" && key in node) {
      return (node as Record<string, unknown>)[key];
    }
    return undefined;
  }, SITE_COPY);
}

/** The raw (HTML) value of a site copy key. */
export function siteCopy(key: SiteCopyKey): string {
  const value = lookup(key);
  if (typeof value !== "string") throw new Error(`Missing site copy key: ${key}`);
  return value;
}

/** A non-string value (array or number), e.g. `siteData<[string, number, number][]>("home.pri.rows")`. */
export function siteData<T>(key: string): T {
  const value = lookup(key);
  if (value === undefined) throw new Error(`Missing site copy key: ${key}`);
  return value as T;
}

type SiteCopyProps = {
  k: SiteCopyKey;
  as?: ElementType;
} & Omit<HTMLAttributes<HTMLElement>, "children" | "dangerouslySetInnerHTML">;

/** Renders a site copy value. The prototype's `data-c="key"` becomes `<SiteCopy k="key" />`. */
export function SiteCopy({ k, as: Tag = "span", ...rest }: SiteCopyProps) {
  return <Tag data-c={k} {...rest} dangerouslySetInnerHTML={{ __html: siteCopy(k) }} />;
}

/** Trusted markup that isn't a copy key (prototype strings built in code, e.g. `&amp;`). */
export function Html({
  html,
  as: Tag = "span",
  ...rest
}: { html: string; as?: ElementType } & Omit<
  HTMLAttributes<HTMLElement>,
  "children" | "dangerouslySetInnerHTML"
>) {
  return <Tag {...rest} dangerouslySetInnerHTML={{ __html: html }} />;
}
